import { Logger } from "../core/Logger";
import { ProductType } from "../core/types";
import type { CalculatorData } from "../modal/types";
import { BaseWidget } from "./BaseWidget";

// Dealer CSS targets these class names, so changing them breaks live dealer stylesheets.
const EMBED_IFRAME_CLASS = "selectfi-embed-iframe";

export class EmbedWidget extends BaseWidget {

    private get productVariant(): 'calculator' | 'form' {
        return this.contentContainer.productType === ProductType.CALCULATOR ? 'calculator' : 'form';
    }

    private addIframeClasses(iframe: HTMLIFrameElement): void {
        iframe.classList.add(EMBED_IFRAME_CLASS, `${EMBED_IFRAME_CLASS}--${this.productVariant}`);
    }

    inject(): void {
        const iframe = this.iframeProvider.createIframe(
            this.dealerKey,
            this.config,
            this.options,
            this.contentContainer
        );
        this.addIframeClasses(iframe);
        iframe.style.width = "100%";
        iframe.style.height = "800px";

        this.injectCustomCss();
        this.contentContainer.element.innerHTML = iframe.outerHTML;
        this.tracker.trackOpen(this.contentContainer);
    }

    // Declarations only, scoped to this product's iframe like ctaCustomCss; nested @media works for breakpoints.
    private injectCustomCss(): void {
        const declarations = this.config.styles[this.productVariant].embedCustomCss;
        const id = `selectfi-embed-custom-css--${this.productVariant}`;
        let style = document.getElementById(id);

        if (typeof declarations !== 'string' || !declarations.trim()) {
            style?.remove();
            return;
        }

        if (!style) {
            style = document.createElement('style');
            style.id = id;
            document.head.appendChild(style);
        }
        const css = `.${EMBED_IFRAME_CLASS}--${this.productVariant} { ${declarations} }`;
        // textContent is never parsed as HTML, so a "</style>" in dealer CSS cannot escape the tag.
        if (style.textContent !== css) {
            style.textContent = css;
        }
    }

    // override implementation since embed does not use modal
    swapCalculatorForScreening(calculatorData?: CalculatorData): void {
        if (this.contentContainer.productType !== ProductType.CALCULATOR) {
            Logger.warn('swapCalculatorForScreening called, but current product type is not CALCULATOR.');
            return;
        }

        this.contentContainer.productType = ProductType.SCREENING;

        // Update iframe
        const iframe = this.iframeProvider.createIframe(
            this.dealerKey,
            this.config,
            this.options,
            this.contentContainer,
            false,
            calculatorData
        );
        this.addIframeClasses(iframe);

        this.injectCustomCss();
        this.contentContainer.element.innerHTML = iframe.outerHTML;
    }

    swapScreeningForCalculator(): void {
        // EmbedWidget doesn't need swapScreeningForCalculator since it doesn't have a modal that closes
        return;
    }
}
